import { BarChart, StatTile } from '../charts/Charts'

interface Driver {
  driver_id: string
  driver_name: string
  trips: number
  km: number | null
  driving_hours: number | null
  overspeed_events: number
  critical_alerts: number
  harsh_brakes: number
  idle_hours: number | null
  night_trips: number
}

interface OverspeedZone {
  zone: string
  events: number
  vehicles: number
  median_excess_kph: number | null
}

export interface DriverData {
  coverage: {
    drivers: number | null
    completed_legs: number | null
    legs_with_a_driver: number | null
    attributed_pct: number | null
    verdict: string | null
  }
  medians: {
    median_critical_alerts: number | null
    median_overspeed_events: number | null
    median_idle_hours: number | null
  }
  overspeed_by_zone: OverspeedZone[]
  total_drivers: number
  drivers: Driver[]
}

export function DriverPreview({ data }: { data: DriverData }): JSX.Element {
  const c = data.coverage
  const m = data.medians

  const topByHours = [...data.drivers].sort((a, b) => (b.driving_hours ?? 0) - (a.driving_hours ?? 0)).slice(0, 8)
  const hoursPoints = topByHours.map((d) => ({
    label: d.driver_name.split(' ')[0] ?? d.driver_name,
    title: d.driver_name,
    values: { hours: d.driving_hours ?? 0, overspeed: d.overspeed_events },
  }))

  const topByIdle = [...data.drivers]
    .filter((d) => (d.idle_hours ?? 0) > 0)
    .sort((a, b) => (b.idle_hours ?? 0) - (a.idle_hours ?? 0))
    .slice(0, 8)
  const idlePoints = topByIdle.map((d) => ({
    label: d.driver_name.split(' ')[0] ?? d.driver_name,
    title: d.driver_name,
    values: { idle: d.idle_hours ?? 0 },
  }))

  const zonePoints = data.overspeed_by_zone.map((z) => ({
    label: z.zone.length > 12 ? `${z.zone.slice(0, 11)}…` : z.zone,
    title: z.zone,
    values: { events: z.events },
  }))

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
        <StatTile label="Drivers on record" value={c.drivers ?? '—'} />
        <StatTile label="Completed legs" value={c.completed_legs ?? '—'} />
        <StatTile
          label="Register attribution"
          value={c.attributed_pct ?? '—'} unit="%"
          tone={(c.attributed_pct ?? 100) < 50 ? 'warn' : 'default'}
        />
        <StatTile label="Drivers this window" value={data.total_drivers} />
      </div>

      <div className="grid grid-cols-3 gap-2.5">
        <StatTile
          label="Median critical alerts / driver" value={m.median_critical_alerts ?? '—'}
          tone={(m.median_critical_alerts ?? 0) > 0 ? 'danger' : 'default'}
        />
        <StatTile
          label="Median overspeed events / driver" value={m.median_overspeed_events ?? '—'}
          tone={(m.median_overspeed_events ?? 0) > 0 ? 'warn' : 'default'}
        />
        <StatTile label="Median idle hours / driver" value={m.median_idle_hours ?? '—'} unit="h" />
      </div>

      {c.verdict && <p className="rounded-md bg-slate-50 px-3 py-2 text-[11.5px] text-slate-600">{c.verdict}</p>}

      <div className="grid gap-3 lg:grid-cols-2">
        <div className="rounded-lg border border-slate-100 p-3">
          <h5 className="mb-2 text-[11px] font-semibold text-slate-700">Driving hours vs overspeed events, top drivers</h5>
          <BarChart
            data={hoursPoints}
            series={[
              { key: 'hours', label: 'Driving hours', color: '#2563EB' },
              { key: 'overspeed', label: 'Overspeed events', color: '#DC2626' },
            ]}
          />
        </div>
        <div className="rounded-lg border border-slate-100 p-3">
          <h5 className="mb-2 text-[11px] font-semibold text-slate-700">Idling hours, top drivers</h5>
          {idlePoints.length === 0 ? (
            <p className="py-6 text-center text-[11px] text-slate-400">No idle time recorded this window.</p>
          ) : (
            <BarChart data={idlePoints} series={[{ key: 'idle', label: 'Idle hours', color: '#D97706' }]} />
          )}
        </div>
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <div className="rounded-lg border border-slate-100 p-3">
          <h5 className="mb-2 text-[11px] font-semibold text-slate-700">All overspeed events, by zone</h5>
          <p className="mb-2 text-[10.5px] text-slate-400">Fleet-wide for this window, not limited to the drivers listed below.</p>
          {zonePoints.length === 0 ? (
            <p className="py-6 text-center text-[11px] text-slate-400">No overspeed events in this window.</p>
          ) : (
            <BarChart data={zonePoints} series={[{ key: 'events', label: 'Events', color: '#0EA5E9' }]} />
          )}
        </div>
        <div className="rounded-lg border border-slate-100">
          <div className="border-b border-slate-100 px-3 py-2">
            <h5 className="text-[11px] font-semibold text-slate-700">Overspeed by zone</h5>
          </div>
          {data.overspeed_by_zone.length === 0 ? (
            <p className="px-3 py-4 text-center text-[11px] text-slate-400">No overspeed events in this window.</p>
          ) : (
            <table className="w-full text-[11.5px]">
              <thead>
                <tr className="border-b border-slate-100 text-[10px] font-medium uppercase tracking-wide text-slate-400">
                  <th className="px-3 py-1.5 text-left">Zone</th>
                  <th className="px-2 py-1.5 text-right">Events</th>
                  <th className="px-2 py-1.5 text-right">Vehicles</th>
                  <th className="px-2 py-1.5 text-right">Median excess</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {data.overspeed_by_zone.map((z) => (
                  <tr key={z.zone} className="transition-colors duration-100 hover:bg-slate-50/60">
                    <td className="px-3 py-1.5 font-medium text-slate-700">{z.zone}</td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-600">{z.events}</td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-600">{z.vehicles}</td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-600">
                      {z.median_excess_kph != null ? `${z.median_excess_kph} kph` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-slate-100">
        <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
          <h5 className="text-[11px] font-semibold text-slate-700">Top drivers</h5>
          <span className="text-[10.5px] text-slate-400">
            {data.drivers.length} of {data.total_drivers} shown — full list in the workbook
          </span>
        </div>
        {data.drivers.length === 0 ? (
          <p className="px-3 py-4 text-center text-[11px] text-slate-400">No attributed legs in this window.</p>
        ) : (
          <table className="w-full text-[11.5px]">
            <thead>
              <tr className="border-b border-slate-100 text-[10px] font-medium uppercase tracking-wide text-slate-400">
                <th className="px-3 py-1.5 text-left">Driver</th>
                <th className="px-2 py-1.5 text-right">Trips</th>
                <th className="px-2 py-1.5 text-right">km</th>
                <th className="px-2 py-1.5 text-right">Hours</th>
                <th className="px-2 py-1.5 text-right">Overspeed</th>
                <th className="px-2 py-1.5 text-right">Critical</th>
                <th className="px-2 py-1.5 text-right">Idle h</th>
                <th className="px-2 py-1.5 text-right">Night trips</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {data.drivers.map((d, i) => (
                // driver_id + index, not driver_id alone: the TIMS register has known data
                // quality gaps (see db/reports_driver_tims.sql) and duplicate/blank ids in
                // real data threw a "two children with the same key" React warning here.
                <tr key={`${d.driver_id}-${i}`} className="transition-colors duration-100 hover:bg-slate-50/60">
                  <td className="px-3 py-1.5 font-medium text-slate-700">{d.driver_name}</td>
                  <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-600">{d.trips}</td>
                  <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-600">{d.km ?? '—'}</td>
                  <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-600">{d.driving_hours ?? '—'}</td>
                  <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-600">
                    {d.overspeed_events > 0 ? <span className="text-amber-600">{d.overspeed_events}</span> : '—'}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-600">
                    {d.critical_alerts > 0 ? <span className="text-rose-600">{d.critical_alerts}</span> : '—'}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-600">{d.idle_hours ?? '—'}</td>
                  <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-600">{d.night_trips || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
